import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import { getFormWorkDay } from "@api";
import { Grid, CircularProgress } from "@material-ui/core";

type Role = {
  id: number;
  name: string;
  group: "roles1" | "roles2" | "roles3";
};

type HrRole = {
  id: number;
  name: string;
  group: "hrRoles1" | "hrRoles2" | "hrRoles3";
};

export type WorkdayFormValues = {
  legalName: string;
  phone?: string;
  location: string;
  email: string;
  jobTitle: string;
  roles1: number[];
  roles2: number[];
  roles3: number[];
  hrRoles1: number[];
  hrRoles2: number[];
  hrRoles3: number[];
  supOrg?: string;
  locOther?: string;
  comments?: string;
};

type Props = {
  onCancel: () => void;
  onSubmit: (values: WorkdayFormValues) => void;
  open: boolean;
  values: Partial<WorkdayFormValues>;
  readonly?: boolean;
};

export const WorkdayForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const [fetching, setFetching] = React.useState(false);
  const [roles, setRoles] = React.useState<(Role | null)[]>([]);
  const [hrRoles, setHrRoles] = React.useState<(HrRole | null)[]>([]);
  const form = Form.useManager<WorkdayFormValues>({
    legalName: "",
    phone: "",
    location: "",
    email: "",
    jobTitle: "",
    roles1: [],
    roles2: [],
    roles3: [],
    hrRoles1: [],
    hrRoles2: [],
    hrRoles3: [],
    supOrg: "",
    locOther: "",
    comments: "",
    ...values,
  });

  React.useEffect(() => {
    setFetching(true);
    getFormWorkDay()
      .then(({ roles1, roles2, roles3, hrRoles1, hrRoles2, hrRoles3 }) => {
        setRoles(
          Array.from({
            length: Math.max(roles1.length, roles2.length, roles3.length),
          }).reduce<(Role | null)[]>((roles, placeholder, index) => {
            return [
              ...roles,
              roles1[index]
                ? {
                    ...roles1[index],
                    group: "roles1",
                  }
                : null,
              roles2[index]
                ? {
                    ...roles2[index],
                    group: "roles2",
                  }
                : null,
              roles3[index]
                ? {
                    ...roles3[index],
                    group: "roles3",
                  }
                : null,
            ];
          }, [])
        );
        setHrRoles(
          Array.from({
            length: Math.max(hrRoles1.length, hrRoles2.length, hrRoles3.length),
          }).reduce<(HrRole | null)[]>((hrRoles, placeholder, index) => {
            return [
              ...hrRoles,
              hrRoles1[index]
                ? {
                    ...hrRoles1[index],
                    group: "hrRoles1",
                  }
                : null,
              hrRoles2[index]
                ? {
                    ...hrRoles2[index],
                    group: "hrRoles2",
                  }
                : null,
              hrRoles3[index]
                ? {
                    ...hrRoles3[index],
                    group: "hrRoles3",
                  }
                : null,
            ];
          }, [])
        );
        setFetching(false);
      })
      .catch((error) => {
        setFetching(false);
        console.log("error", error);
      });
  }, []);

  function onFormTextboxValueChange(
    name: keyof WorkdayFormValues,
    value: any
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function onFormCheckboxValueChange(
    role: Role | HrRole,
    checked: boolean
  ): void {
    if (role) {
      const name = role.group;
      const value = checked
        ? form.values[name].concat(role.id)
        : form.values[name].filter((id) => id !== role.id);

      form.set(Form.target.VALUE, [name, value]);
      validate(name, value).then(([name, error]) => {
        form.set(Form.target.ERROR, [name, error]);
      });
    }
  }

  function validate(
    name: keyof WorkdayFormValues,
    value: WorkdayFormValues[typeof name]
  ): Promise<[keyof WorkdayFormValues, Error?]> {
    switch (name) {
      case "legalName":
      case "location":
      case "jobTitle":
        return Promise.resolve([name, Form.Validators.hasValue(value)]);
      case "roles1":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(
            (value as number[]).concat(form.values.roles2, form.values.roles3)
          ),
        ]);
      case "roles2":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(
            (value as number[]).concat(form.values.roles1, form.values.roles3)
          ),
        ]);
      case "roles3":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(
            (value as number[]).concat(form.values.roles1, form.values.roles2)
          ),
        ]);
      case "email":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEmail(value as string),
        ]);
      default:
        return Promise.resolve([name]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as keyof WorkdayFormValues, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);
      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  function getRolesError(): Error | undefined {
    return !form.errors.roles1 || !form.errors.roles2 || !form.errors.roles3
      ? undefined
      : form.errors.roles1 || form.errors.roles2 || form.errors.roles3;
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="Workday User Access Request Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1. Information</Form.Title>
        <Form.Text color="#80C8F3">
          Please complete this form to add security profile in Workday.
        </Form.Text>
        <Form.Textbox
          label="Legal Name"
          name="legalName"
          onChange={onFormTextboxValueChange}
          size={6}
          value={form.values.legalName}
          error={form.errors.legalName}
          required
        />
        <Form.Textbox
          label="Phone (optional)"
          name="phone"
          onChange={onFormTextboxValueChange}
          size={6}
          value={form.values.phone}
          error={form.errors.phone}
        />
        <Form.Textbox
          label="Location"
          name="location"
          onChange={onFormTextboxValueChange}
          size={6}
          value={form.values.location}
          error={form.errors.location}
          required
        />
        <Form.Textbox
          label="Email"
          name="email"
          onChange={onFormTextboxValueChange}
          size={6}
          value={form.values.email}
          error={form.errors.email}
          required
        />
        <Form.Textbox
          label="Job Title"
          name="jobTitle"
          onChange={onFormTextboxValueChange}
          size={6}
          value={form.values.jobTitle}
          error={form.errors.jobTitle}
          required
        />
        <Form.Title error={getRolesError()}>2. Role Section</Form.Title>
        <Form.Text color="#80C8F3">
          Please check the apropiate roles needed.
        </Form.Text>
        {fetching ? (
          <Grid item xs={12}>
            <CircularProgress size={20} />
          </Grid>
        ) : null}
        {roles.map((role, index) =>
          role ? (
            <Form.Checkbox
              key={role.id}
              label={role.name}
              name={role.group}
              onChange={(name, value) => {
                onFormCheckboxValueChange(role, value);
              }}
              size={4}
              value={form.values[role.group].includes(role.id)}
            />
          ) : (
            <Grid key={`empty:${index}`} item xs={4} />
          )
        )}
        <Form.Title>3. For HRIS Use Only</Form.Title>
        <Form.Text color="#80C8F3">
          Please check the apropiate roles needed.
        </Form.Text>
        {fetching ? (
          <Grid item xs={12}>
            <CircularProgress size={20} />
          </Grid>
        ) : null}
        {hrRoles.map((hrRole, index) =>
          hrRole ? (
            <Form.Checkbox
              key={hrRole.id}
              label={hrRole.name}
              name={hrRole.group}
              onChange={(name, value) => {
                onFormCheckboxValueChange(hrRole, value);
              }}
              size={4}
              value={form.values[hrRole.group].includes(hrRole.id)}
            />
          ) : (
            <Grid key={`null:${index}`} item xs={4} />
          )
        )}
        <Form.Title>4. Profile Restriction</Form.Title>
        <Form.Text color="#80C8F3">
          Please provide the relevant information requested below.
        </Form.Text>
        <Form.Textarea
          label="Supervisory Organization (US Location Only)"
          maxLength={200}
          name="supOrg"
          onChange={onFormTextboxValueChange}
          placeholder={"Write details here."}
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.supOrg}
        />
        <Form.Textarea
          label="Location (All Other Countries)"
          maxLength={200}
          name="locOther"
          onChange={onFormTextboxValueChange}
          placeholder={"Write details here."}
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.locOther}
        />
        <Form.Textarea
          label="Comments"
          maxLength={200}
          name="comments"
          onChange={onFormTextboxValueChange}
          placeholder={"Write details here."}
          rows={5}
          rowsMax={5}
          size={12}
          value={form.values.comments}
        />
      </Form>
    </Wrapper>
  );
};
